import { useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Close, KeyboardArrowDown, Search } from '@mui/icons-material';
import {
  useDelayedCommitValue,
  useScrollHiddenItemCounts,
  useSearchableLimitedOptions,
  useSearchableListKeyboardNavigation,
} from 'components/ui/hooks';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
} from 'components/ui/PrimaryNavigation';
import { ScrollHiddenItemsHint } from 'components/ui/ScrollHiddenItemsHint';
import { Skeleton } from 'components/ui/Skeleton';
import { Tooltip } from 'components/ui/Tooltip';
import { getCssPx } from 'components/ui/utilities/zoom';
import { DEFAULT_ZOOM_LEVEL } from 'context/ZoomContext';
import { PrismThemeProvider } from 'theme';
import {
  ENTITY_LIST_PANEL_LABELS,
  ENTITY_LIST_PANEL_SKELETON_ITEM_COUNT,
} from './EntityListPanel.constants';
import type { EntityListPanelProps } from './EntityListPanel.props';
import {
  EntityListPanelControls,
  EntityListPanelEndContent,
  EntityListPanelFilterButton,
  EntityListPanelFilterButtonLabel,
  EntityListPanelHeader,
  EntityListPanelHeaderActions,
  EntityListPanelItemButton,
  EntityListPanelItemLabel,
  EntityListPanelList,
  EntityListPanelListFrame,
  EntityListPanelMeta,
  EntityListPanelRoot,
  EntityListPanelSearch,
  EntityListPanelSearchClearButton,
  EntityListPanelSearchInput,
  EntityListPanelSkeletonRow,
  EntityListPanelState,
  EntityListPanelStateDescription,
  EntityListPanelStateTitle,
  EntityListPanelTitle,
} from './EntityListPanel.styles';
import type { EntityListPanelFilterValue, EntityListPanelItem as EntityListPanelItemType } from './EntityListPanel.types';

const EntityListPanelSkeleton = ({ itemCount }: { itemCount: number }) => (
  <EntityListPanelList>
    {Array.from({ length: itemCount }, (_, index) => (
      <EntityListPanelSkeletonRow key={index}>
        <Skeleton
          height={getCssPx(14)}
          variant="text"
          width={index % 2 ? '58%' : '76%'}
        />
        <Skeleton
          height={getCssPx(18)}
          radius={getCssPx(4)}
          width={getCssPx(index % 2 ? 92 : 116)}
        />
        <Skeleton
          height={getCssPx(14)}
          variant="text"
          width={getCssPx(48)}
        />
      </EntityListPanelSkeletonRow>
    ))}
  </EntityListPanelList>
);

const EntityListPanelStateMessage = ({
  description,
  testId,
  title,
}: {
  description: ReactNode;
  testId?: string;
  title: ReactNode;
}) => (
  <EntityListPanelState data-testid={testId}>
    <EntityListPanelStateTitle>{title}</EntityListPanelStateTitle>
    <EntityListPanelStateDescription>{description}</EntityListPanelStateDescription>
  </EntityListPanelState>
);

const getStringValue = (value: EntityListPanelFilterValue) => (value === null ? 'all' : String(value));
const getTextTooltipTitle = (content: ReactNode) => (typeof content === 'string' ? content : '');
const getSearchableFilterLabel = (label: ReactNode) => (typeof label === 'string' ? label : '');
const ENTITY_LIST_PANEL_ITEM_SELECTOR = '[data-entity-list-panel-item="true"]';

const EntityListPanelItem = ({
  isSelected,
  item,
  onItemSelect,
  testIds,
}: {
  isSelected: boolean;
  item: EntityListPanelItemType;
  onItemSelect?: EntityListPanelProps['onItemSelect'];
  testIds?: EntityListPanelProps['testIds'];
}) => {
  const labelRef = useRef<HTMLElement | null>(null);

  return (
    <OverflowTooltip measureRef={labelRef} title={getTextTooltipTitle(item.label)}>
      <EntityListPanelItemButton
        aria-label={item.ariaLabel}
        aria-pressed={isSelected}
        data-entity-list-panel-item="true"
        data-testid={testIds?.item?.(item.id)}
        disabled={item.disabled}
        isSelected={isSelected}
        onClick={() => onItemSelect?.(item)}>
        <EntityListPanelItemLabel ref={labelRef}>{item.label}</EntityListPanelItemLabel>
        {item.metaContent ? <EntityListPanelMeta>{item.metaContent}</EntityListPanelMeta> : null}
        {item.endContent !== undefined && item.endContent !== null ? (
          <EntityListPanelEndContent>{item.endContent}</EntityListPanelEndContent>
        ) : null}
      </EntityListPanelItemButton>
    </OverflowTooltip>
  );
};

const EntityListPanel = ({
  emptyDescription = ENTITY_LIST_PANEL_LABELS.emptyDescription,
  emptyTitle = ENTITY_LIST_PANEL_LABELS.emptyTitle,
  errorDescription = ENTITY_LIST_PANEL_LABELS.errorDescription,
  errorTitle = ENTITY_LIST_PANEL_LABELS.errorTitle,
  filterAriaLabel = ENTITY_LIST_PANEL_LABELS.filter,
  filterEmptyLabel = ENTITY_LIST_PANEL_LABELS.emptyFilterOptions,
  filterLabel = ENTITY_LIST_PANEL_LABELS.filter,
  filterOptions = [],
  filterSearchPlaceholder = ENTITY_LIST_PANEL_LABELS.filterSearchPlaceholder,
  filterValue = null,
  isError = false,
  isLoading = false,
  items,
  onFilterChange,
  onItemSelect,
  onSearchChange,
  searchCommitDelayMs = 0,
  searchPlaceholder = ENTITY_LIST_PANEL_LABELS.searchPlaceholder,
  searchValue = '',
  scrollHintItemLabelPlural = ENTITY_LIST_PANEL_LABELS.scrollHintItemPlural,
  scrollHintItemLabelSingular = ENTITY_LIST_PANEL_LABELS.scrollHintItemSingular,
  selectedItemId,
  skeletonItemCount = ENTITY_LIST_PANEL_SKELETON_ITEM_COUNT,
  testIds,
  title,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
}: EntityListPanelProps) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const filterSearchInputRef = useRef<HTMLInputElement>(null);
  const {
    handleItemsKeyDown,
    handleSearchKeyDown,
  } = useSearchableListKeyboardNavigation<HTMLButtonElement>({
    inputRef: searchInputRef,
    itemSelector: ENTITY_LIST_PANEL_ITEM_SELECTOR,
    listRef,
  });
  const { hiddenItemCounts, scrollToHiddenItems, updateHiddenItemCounts } = useScrollHiddenItemCounts<HTMLDivElement>({
    isEnabled: !isLoading && !isError && items.length > 0,
    itemSelector: ENTITY_LIST_PANEL_ITEM_SELECTOR,
    scrollContainerRef: listRef,
  });
  const selectedFilterLabel = useMemo(
    () => (filterValue === null
      ? filterLabel
      : filterOptions.find(option => option.value === filterValue)?.label ?? filterLabel),
    [filterLabel, filterOptions, filterValue]
  );
  const {
    filteredOptions: filteredFilterOptions,
    resetSearchableOptions: resetFilterOptions,
    searchValue: filterSearchValue,
    setSearchValue: setFilterSearchValue,
  } = useSearchableLimitedOptions({
    getSearchText: option => getSearchableFilterLabel(option.label),
    options: filterOptions,
  });
  const isFilterOpen = Boolean(filterAnchorEl);
  const shouldShowFilter = filterOptions.length > 0;
  const {
    setValue: setSearchInputValue,
    value: resolvedSearchValue,
  } = useDelayedCommitValue({
    commitDelayMs: searchCommitDelayMs,
    onCommit: onSearchChange,
    value: searchValue,
  });

  const handleFilterOpen = (event: MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    resetFilterOptions();
  };

  const handleFilterChange = (value: EntityListPanelFilterValue) => {
    onFilterChange?.(value);
    handleFilterClose();
  };

  const handleSearchChange = (value: string) => {
    setSearchInputValue(value);
  };

  const handleSearchClear = () => {
    setSearchInputValue('');
    searchInputRef.current?.focus();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div data-testid={testIds?.skeleton}>
          <EntityListPanelSkeleton itemCount={skeletonItemCount} />
        </div>
      );
    }

    if (isError) {
      return (
        <EntityListPanelStateMessage
          description={errorDescription}
          testId={testIds?.errorState}
          title={errorTitle}
        />
      );
    }

    if (!items.length) {
      return (
        <EntityListPanelStateMessage
          description={emptyDescription}
          testId={testIds?.emptyState}
          title={emptyTitle}
        />
      );
    }

    return (
      <EntityListPanelListFrame>
        {hiddenItemCounts.above > 0 ? (
          <ScrollHiddenItemsHint
            count={hiddenItemCounts.above}
            itemLabelPlural={scrollHintItemLabelPlural}
            itemLabelSingular={scrollHintItemLabelSingular}
            onClick={() => scrollToHiddenItems('above')}
            placement="top"
          />
        ) : null}
        <EntityListPanelList
          ref={listRef}
          data-testid={testIds?.list}
          onKeyDown={handleItemsKeyDown}
          onScroll={updateHiddenItemCounts}>
          {items.map(item => {
            const isSelected = selectedItemId === item.id;

            return (
              <EntityListPanelItem
                key={item.id}
                isSelected={isSelected}
                item={item}
                onItemSelect={onItemSelect}
                testIds={testIds}
              />
            );
          })}
        </EntityListPanelList>
        {hiddenItemCounts.below > 0 ? (
          <ScrollHiddenItemsHint
            count={hiddenItemCounts.below}
            itemLabelPlural={scrollHintItemLabelPlural}
            itemLabelSingular={scrollHintItemLabelSingular}
            onClick={() => scrollToHiddenItems('below')}
            placement="bottom"
          />
        ) : null}
      </EntityListPanelListFrame>
    );
  };

  return (
    <PrismThemeProvider>
      <EntityListPanelRoot data-testid={testIds?.root}>
        <EntityListPanelHeader>
          <OverflowTooltip title={getTextTooltipTitle(title)}>
            <EntityListPanelTitle>{title}</EntityListPanelTitle>
          </OverflowTooltip>
          {shouldShowFilter ? (
            <EntityListPanelHeaderActions>
              <EntityListPanelFilterButton
                aria-controls={isFilterOpen ? testIds?.filterMenu : undefined}
                aria-expanded={isFilterOpen}
                aria-haspopup="menu"
                aria-label={filterAriaLabel}
                data-testid={testIds?.filterButton}
                onClick={handleFilterOpen}>
                <OverflowTooltip title={getTextTooltipTitle(selectedFilterLabel)}>
                  <EntityListPanelFilterButtonLabel>{selectedFilterLabel}</EntityListPanelFilterButtonLabel>
                </OverflowTooltip>
                <KeyboardArrowDown fontSize="inherit" />
              </EntityListPanelFilterButton>
              <PrimaryNavigationMenu
                anchorEl={filterAnchorEl}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                data-testid={testIds?.filterMenu}
                disableAutoFocusItem
                id={testIds?.filterMenu}
                menuMinWidth={200}
                open={isFilterOpen}
                scrollHintItemLabelPlural="options"
                scrollHintItemLabelSingular="option"
                zoomLevel={zoomLevel}
                onClose={handleFilterClose}
                TransitionProps={{ onEntered: () => filterSearchInputRef.current?.focus() }}
                MenuListProps={{
                  disableListWrap: true,
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
                <PrimaryNavigationMenuSearch
                  clearAriaLabel="Clear filter search"
                  inputRef={filterSearchInputRef}
                  data-testid={testIds?.filterSearchInput}
                  fullWidth
                  onChange={event => setFilterSearchValue(event.target.value)}
                  onClear={() => setFilterSearchValue('')}
                  placeholder={filterSearchPlaceholder}
                  value={filterSearchValue}
                />
                {filteredFilterOptions.map(option => (
                  <PrimaryNavigationMenuItem
                    key={getStringValue(option.value)}
                    data-testid={testIds?.filterOption?.(option.value)}
                    isActive={option.value === filterValue}
                    onClick={() => handleFilterChange(option.value)}
                    selected={option.value === filterValue}>
                    {option.label}
                  </PrimaryNavigationMenuItem>
                ))}
                {filteredFilterOptions.length === 0 && (
                  <PrimaryNavigationMenuItem disabled tone="muted">
                    {filterEmptyLabel}
                  </PrimaryNavigationMenuItem>
                )}
              </PrimaryNavigationMenu>
            </EntityListPanelHeaderActions>
          ) : null}
        </EntityListPanelHeader>
        <EntityListPanelControls>
          <EntityListPanelSearch>
            <Search fontSize="small" />
            <EntityListPanelSearchInput
              aria-label={searchPlaceholder}
              data-testid={testIds?.searchInput}
              onChange={event => handleSearchChange(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              ref={searchInputRef}
              value={resolvedSearchValue}
            />
            {resolvedSearchValue ? (
              <Tooltip placement="bottom" title="Clear search">
                <EntityListPanelSearchClearButton
                  aria-label="Clear search"
                  onClick={handleSearchClear}
                  onMouseDown={event => event.preventDefault()}
                  type="button">
                  <Close fontSize="inherit" />
                </EntityListPanelSearchClearButton>
              </Tooltip>
            ) : null}
          </EntityListPanelSearch>
        </EntityListPanelControls>
        {renderContent()}
      </EntityListPanelRoot>
    </PrismThemeProvider>
  );
};

export default EntityListPanel;
